import { useMemo } from 'react';
import * as fmt from '../lib/format.js';
import {
  estimateEnergy,
  mean,
  movingAverage,
  type BodyProfile,
  type DayRow,
} from '../lib/stats.js';

interface Props {
  series: DayRow[];
  profile: BodyProfile;
}

/** Recul du point de comparaison de la tuile Δ. */
const LOOKBACK = 30;

export function StatsRow({ series, profile }: Props) {
  const stats = useMemo(() => {
    const last = series.length - 1;
    const average = movingAverage(series, 7);

    // Deux moyennes 7 jours, jamais deux pesées : comparer deux mesures
    // isolées additionne leur bruit, ~0,5 % du poids chacune, soit plus que
    // l'écart qu'on cherche à lire sur un mois.
    const now = average[last] ?? null;
    const before = average[last - LOOKBACK] ?? null;
    const change = now != null && before != null ? now - before : null;
    const reference = series[last - LOOKBACK] ?? null;

    // Dernière pesée connue, pas forcément celle d'aujourd'hui.
    const latest = [...series].reverse().find((row) => row.weight != null) ?? null;

    const lastWeek = series.slice(-7);
    const stepsAverage = mean(lastWeek.map((row) => row.steps));
    const logged = series.slice(-30).filter((row) => row.weight != null).length;
    const energy = estimateEnergy(series, last, profile);

    return [
      {
        label: 'Poids · moy. 7 j',
        value: now == null ? '—' : `${fmt.weight(now)} kg`,
        sub:
          latest?.weight == null
            ? 'aucune pesée'
            : `dernière pesée ${fmt.measuredWeight(latest.weight)} le ${fmt.dayMonth(latest.date)}`,
      },
      {
        label: `Δ ${LOOKBACK} jours`,
        value: change == null ? '—' : `${fmt.delta(change)} kg`,
        sub:
          change == null
            ? 'moyenne 7 j indisponible aux deux bouts'
            : `moy. 7 j vs ${fmt.dayMonth(reference?.date ?? new Date())}`,
      },
      {
        label: 'Pas / jour',
        value: fmt.number(stepsAverage),
        sub: 'moyenne 7 jours',
      },
      {
        label: 'Maintien',
        value: `${fmt.number(energy.tdee)} kcal`,
        sub:
          energy.source === 'observed'
            ? `déduit de ${energy.days} j de saisies`
            : energy.restingSource === 'mifflin'
              ? 'modèle morphologie + pas'
              : 'modèle approximatif — profil incomplet',
      },
      {
        label: 'Suivi',
        value: `${logged}/30`,
        sub: 'jours pesés',
      },
    ];
  }, [series, profile]);

  return (
    <div className="stats">
      {stats.map((stat) => (
        <div className="stat" key={stat.label}>
          <div className="stat__label">{stat.label}</div>
          <div className="stat__value">{stat.value}</div>
          <div className="stat__sub">{stat.sub}</div>
        </div>
      ))}
    </div>
  );
}
